import { RegisterableHotkey } from '@tanstack/react-hotkeys';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { useLocalStorage } from '@shared/util/storage/useLocalStorage';
import { HotkeyActionId } from './hotkeyActions';

// A user's hotkey override is a valid key, or null, or absent. Absent means the
// user has no override, so the default is used, if defined. Null means the user
// cleared the default binding, so nothing is bound.
export type HotkeyOverride = RegisterableHotkey | null;
export type HotkeyOverrides = Partial<Record<HotkeyActionId, HotkeyOverride>>;

/**
 * Everything the Preferences hotkeys section persists, in one localStorage
 * record. `overrides` is optional because its default is empty.
 */
export interface HotkeySettings {
  overrides?: HotkeyOverrides;
  /** While off (or the feature is off), tooltips hide the key cap hint. */
  showTooltips: boolean;
}

const HOTKEY_SETTINGS_STORAGE_KEY = 'preferences-hotkeys';

const DEFAULT_HOTKEY_SETTINGS: HotkeySettings = {
  showTooltips: true,
};

const EMPTY_OVERRIDES: HotkeyOverrides = {};

/**
 * A single settings field and its setter, similar to `useState`.
 */
export type SettingsField<T> = readonly [T, Dispatch<SetStateAction<T>>];

/**
 * Wraps a setter so its updater sees the read fallback rather than `undefined`.
 * Only needed for the optional `overrides` field.
 */
function useDefaultedSetter<T>(
  setField: Dispatch<SetStateAction<T | undefined>>,
  fallback: T,
): Dispatch<SetStateAction<T>> {
  return useCallback(
    (update: SetStateAction<T>) =>
      setField(prev => (update instanceof Function ? update(prev ?? fallback) : update)),
    [setField, fallback],
  );
}

/**
 * Reads and writes the whole Preferences hotkeys record. Prefer the
 * field-scoped hooks below, which write one field without clobbering the rest.
 */
export function useHotkeySettings() {
  return useLocalStorage<HotkeySettings>(HOTKEY_SETTINGS_STORAGE_KEY, DEFAULT_HOTKEY_SETTINGS);
}

function useHotkeySettingsField<K extends keyof HotkeySettings>(
  field: K,
): SettingsField<HotkeySettings[K]> {
  const [settings, setSettings] = useHotkeySettings();
  const setField = useCallback(
    (update: SetStateAction<HotkeySettings[K]>) =>
      setSettings(prev => ({
        ...prev,
        [field]: update instanceof Function ? update(prev[field]) : update,
      })),
    [setSettings, field],
  );
  return [settings[field], setField];
}

/** The user's recorded keybinding overrides (see the Preferences hotkeys section). */
export function useHotkeyOverrides(): SettingsField<HotkeyOverrides> {
  const [overrides, setOverrides] = useHotkeySettingsField('overrides');
  return [overrides ?? EMPTY_OVERRIDES, useDefaultedSetter(setOverrides, EMPTY_OVERRIDES)];
}

/**
 * Whether tooltips (`TooltipPresenter`) show the key cap hint alongside their text.
 */
export function useShowHotkeyTooltips(): SettingsField<boolean> {
  return useHotkeySettingsField('showTooltips');
}
